import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

export const fuzzySearch = (options) => {
  return (value = "") => {
    const term = value.toString().toLowerCase().trim();
    if (!term) return options;

    return options.filter((option) => {
      const label =
        option?.name ??
        option?.label ??
        (typeof option === "string" ? option : "");
      return label.toString().toLowerCase().includes(term);
    });
  };
};

const getOptionLabel = (option) =>
  option?.name ?? option?.label ?? option?.value ?? "";

const SelectSearch = ({
  options = [],
  value = "",
  onChange,
  placeholder = "",
  inputId,
  ariaLabel,
  search = true,
  filterOptions = fuzzySearch,
  renderOption,
  name,
  autoComplete = "on",
  disabled = false,
  className = "",
  style,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedOption = useMemo(
    () => options.find((opt) => opt?.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const filterFn =
      typeof filterOptions === "function"
        ? filterOptions(options)
        : (opts) => opts;
    const result = filterFn?.(searchTerm ?? "");
    return Array.isArray(result) ? result : options;
  }, [filterOptions, options, search, searchTerm]);

  useEffect(() => {
    if (!isOpen) return;
    const selectedIdx = filteredOptions.findIndex(
      (opt) => opt?.value === value,
    );
    if (selectedIdx >= 0) {
      setHighlightedIndex(selectedIdx);
    } else if (filteredOptions.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions, isOpen, value]);

  const closeList = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm(null);
  };

  const handleOutsideClick = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      closeList();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  });

  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleSelect = (option) => {
    if (disabled) return;
    const nextValue = option?.value ?? option;
    onChange?.(nextValue);
    closeList();
  };

  const handleKeyDown = (event) => {
    if (!isOpen && ["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      setIsOpen(true);
      setHighlightedIndex(0);
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          Math.min(prev + 1, filteredOptions.length - 1),
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        event.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        closeList();
        break;
      default:
        break;
    }
  };

  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : "";
  const searchValue = searchTerm !== null ? searchTerm : selectedLabel;
  const inputValue = search ? searchValue : selectedLabel;

  return (
    <div
      ref={containerRef}
      className={`select-search ${disabled ? "is-disabled" : ""} ${
        isOpen ? "is-open" : ""
      } ${className}`}
      style={style}
    >
      <div className="select-search__value">
        <input
          id={inputId}
          name={name}
          className="select-search__input"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onClick={() => {
            if (!disabled) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          readOnly={!search}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-label={ariaLabel}
          type="search"
        />
      </div>
      {isOpen && !disabled && (
        <div className="select-search__select">
          <ul className="select-search__options">
            {filteredOptions.map((option, index) => {
              const selected = option?.value === value;
              const highlighted = index === highlightedIndex;
              const itemKey = option?.value ?? option?.name ?? index;
              const optionLabel = String(getOptionLabel(option));
              const optionClass = [
                "select-search__option",
                selected ? "is-selected" : "",
                highlighted ? "is-highlighted" : "",
              ]
                .filter(Boolean)
                .join(" ");

              const optionProps = {
                className: optionClass,
                onMouseDown: (e) => e.preventDefault(),
                onClick: () => handleSelect(option),
                onMouseEnter: () => setHighlightedIndex(index),
                type: "button",
              };

              return (
                <li className="select-search__row" key={itemKey}>
                  {renderOption ? (
                    renderOption(
                      optionProps,
                      option,
                      { selected, highlighted },
                      optionClass,
                    )
                  ) : (
                    <button
                      className={optionClass}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      type="button"
                    >
                      {optionLabel}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

SelectSearch.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  inputId: PropTypes.string,
  ariaLabel: PropTypes.string,
  search: PropTypes.bool,
  filterOptions: PropTypes.func,
  renderOption: PropTypes.func,
  name: PropTypes.string,
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default SelectSearch;
